import { NgTemplateOutlet } from '@angular/common';
import { buildDeleteConfig } from '../../utils/delete-confirmation';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  inject,
  input,
  model,
  NgZone,
  OnDestroy,
  OnInit,
  output,
  TemplateRef,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { ButtonModule } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { TooltipModule } from 'primeng/tooltip';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { Subscription } from 'rxjs';
import { BusinessRulesService } from '../../services/business-rules.service';
import { EntityDialogData } from '../../services/entity-dialog.service';
import { StoreInterfaceService } from '../../services/store-interface.service';
import { BusinessRuleType, EntityDefinition, FieldConfig, SelectOption } from '../../types/entity-definition.interface';
import { EntityFormDeleteAction, EntityFormValue } from '../../types/entity-form-result.interface';
import { GenericEntity } from '../../types/generic-entity.interface';
import { CesDeleteButtonComponent } from '../ui/ces-buttons';
import { CesDialogFooterComponent } from '../ui/dialog-footer/dialog-footer.component';
import { CesHourpickerFieldComponent } from '../ui/ces-hourpicker-field/ces-hourpicker-field.component';
import { CesSelectFieldComponent } from '../ui/ces-select-field/ces-select-field.component';
import { CesTextFieldComponent } from '../ui/ces-text-field/ces-text-field.component';
import { CesDatepickerComponent } from '../ui/inputs/datepicker/datepicker.component';

import { CesTooltipAutoDismissDirective } from '../../directives/ces-tooltip-auto-dismiss.directive';
type FieldErrors = Record<string, string>;
type ExternalErrorMap = Record<string, FieldErrors>;
type SelectOptionList = SelectOption[];

@Component({
  selector: 'app-generic-entity-form',
  standalone: true,
  imports: [
    CesTooltipAutoDismissDirective,
    ButtonModule,
    CesDatepickerComponent,
    CesDeleteButtonComponent,
    CesDialogFooterComponent,
    CesHourpickerFieldComponent,
    CesSelectFieldComponent,
    CesTextFieldComponent,
    NgTemplateOutlet,
    DropdownModule,
    InputNumberModule,
    InputTextModule,
    MultiSelectModule,
    ReactiveFormsModule,
    SelectModule,
    TextareaModule,
    ToggleSwitchModule,
    TooltipModule,
    TranslocoModule,
  ],
  templateUrl: './generic-entity-form.component.html',
  styleUrl: './generic-entity-form.component.scss',
})
export class GenericEntityFormComponent<T extends GenericEntity> implements OnInit, OnDestroy, AfterViewInit {
  protected fb = inject(FormBuilder);
  protected dialogRef = inject<DynamicDialogRef<GenericEntityFormComponent<T>> | null>(DynamicDialogRef, { optional: true });
  protected dialogConfig = inject<DynamicDialogConfig<EntityDialogData<T>> | null>(DynamicDialogConfig, { optional: true });
  protected storeService = inject(StoreInterfaceService);
  protected businessRulesService = inject(BusinessRulesService);
  protected translateService = inject(TranslocoService);

  private zone = inject(NgZone);
  private cdr = inject(ChangeDetectorRef);
  private initialValidationDone = false;

  readonly entityDefinition = model<EntityDefinition<T>>();
  readonly entity = model<T>();
  readonly parentId = model<number>();
  readonly mode = input<'create' | 'edit'>('create');
  readonly additionalData = model<Record<string, unknown>>();
  readonly additionalFieldsTemplate = input<TemplateRef<{ $implicit: FormGroup }>>();

  readonly formSubmit = output<EntityFormValue<T>>();
  readonly formCancel = output<void>();
  readonly deleteRequest = output<T>();

  form!: FormGroup;
  submitting = false;
  errorMessage = '';
  isCreateMode = true;

  private typeOptionsCache: Record<string, SelectOptionList> = {};
  protected externalErrors: ExternalErrorMap = {};
  private formValueChangesSub?: Subscription;
  private validationFields: string[] = [];
  private viewInitTimeout?: ReturnType<typeof setTimeout>;

  ngOnInit(): void {
    if (this.dialogRef && this.dialogConfig?.data) {
      const data = this.dialogConfig.data;
      this.entityDefinition.set(data.entityDefinition);
      if (data.entity !== undefined) this.entity.set(data.entity);
      if (data.parentId !== undefined) this.parentId.set(data.parentId);
      this.isCreateMode = data.mode === 'create';
      if (data.additionalData !== undefined) this.additionalData.set(data.additionalData);
    } else {
      this.isCreateMode = this.mode() === 'create';
    }

    this.initForm();
    this.loadTypeOptions();
  }

  ngAfterViewInit(): void {
    // On applique la validation business après l'initialisation de la vue
    // en utilisant setTimeout pour décaler l'exécution dans le prochain cycle
    this.viewInitTimeout = setTimeout(() => {
      this.applyBusinessRuleValidation();
      this.subscribeToFormChanges();
      this.initialValidationDone = true;
      this.cdr.detectChanges();
    });
  }

  ngOnDestroy(): void {
    if (this.viewInitTimeout !== undefined) clearTimeout(this.viewInitTimeout);
    this.formValueChangesSub?.unsubscribe();
  }

  protected initForm(): void {
    type FormControlConfig = [unknown] | [unknown, ValidatorFn[]] | [{ value: unknown; disabled: true }, ValidatorFn[]];
    const formConfig: Record<string, FormControlConfig> = {};
    const definition = this.entityDefinition();
    const parentId = this.parentId();

    if (parentId && definition?.childRelation) {
      const parentIdField = definition.childRelation.childParentIdField;
      formConfig[parentIdField] = [parentId];
    }

    if (definition?.fields) {
      for (const fieldConfig of definition.fields) {
        if (fieldConfig.field === 'name' && formConfig['name']) continue;

        const validators: ValidatorFn[] = [];
        const condition = fieldConfig.condition;
        const skipWhenHidden = (validator: ValidatorFn): ValidatorFn =>
          condition
            ? control => {
                const formValue = (control.parent?.getRawValue() ?? {}) as Record<string, unknown>;
                return condition(formValue) ? validator(control) : null;
              }
            : validator;

        if (fieldConfig.required) validators.push(skipWhenHidden(Validators.required));
        if (fieldConfig.minLength) validators.push(skipWhenHidden(Validators.minLength(fieldConfig.minLength)));
        if (fieldConfig.maxLength) validators.push(skipWhenHidden(Validators.maxLength(fieldConfig.maxLength)));
        if (fieldConfig.minValue) validators.push(skipWhenHidden(Validators.min(fieldConfig.minValue)));
        if (fieldConfig.maxValue) validators.push(skipWhenHidden(Validators.max(fieldConfig.maxValue)));
        if (fieldConfig.validator) validators.push(skipWhenHidden(fieldConfig.validator));

        const defaultValue = this.resolveFieldDefaultValue(fieldConfig);

        if (
          fieldConfig.type === 'hidden' &&
          fieldConfig.required &&
          (defaultValue === '' || defaultValue === null || defaultValue === undefined)
        ) {
          console.warn(this.translateService.translate('common.errors.hiddenFieldRequiredNoDefault', { field: fieldConfig.field }));
        }

        const disabledFlag =
          typeof fieldConfig.disabled === 'function'
            ? fieldConfig.disabled((this.entity() ?? null) as Record<string, unknown> | null)
            : (fieldConfig.disabled ?? false);
        formConfig[fieldConfig.field] = disabledFlag
          ? [{ value: defaultValue, disabled: true }, validators]
          : [defaultValue, validators];
      }
    }

    if (parentId && definition?.childRelation) {
      const parentIdField = definition.childRelation.childParentIdField;
      formConfig[parentIdField] = [parentId];
    }

    this.form = this.fb.group(formConfig);
  }

  private resolveFieldDefaultValue(fieldConfig: FieldConfig): unknown {
    const fieldName = fieldConfig.field as keyof T & string;
    const entity = this.entity();
    const additionalData = this.additionalData();

    if (entity && entity[fieldName] !== undefined) {
      return entity[fieldName];
    }

    if (fieldConfig.defaultValue !== undefined) {
      return fieldConfig.defaultValue;
    }

    if (fieldConfig.type === 'boolean') return false;
    if (fieldConfig.type === 'number') return 0;

    if (fieldConfig.type === 'datetime' && additionalData?.[fieldConfig.field] !== undefined) {
      const dateValue = additionalData[fieldConfig.field];
      return dateValue instanceof Date ? dateValue.toISOString() : dateValue;
    }

    if (fieldConfig.type === 'hidden' && additionalData?.[fieldConfig.field] !== undefined) {
      return additionalData[fieldConfig.field];
    }

    if (fieldConfig.type === 'multi' && additionalData?.['entityIds']) {
      return additionalData['entityIds'];
    }

    return '';
  }

  protected applyBusinessRuleValidation(): void {
    const definition = this.entityDefinition();
    if (!definition?.validationRules || !this.storeService) {
      return;
    }

    const validationFields = definition.validationFields ?? ['name'];
    this.validationFields = validationFields;

    this.validateBusinessRules();
  }

  private subscribeToFormChanges(): void {
    const conditionalFields = (this.entityDefinition()?.fields ?? []).filter(f => f.condition).map(f => f.field);
    this.formValueChangesSub = this.form.valueChanges.subscribe(() => {
      this.zone.run(() => {
        for (const fieldName of conditionalFields) {
          this.form.get(fieldName)?.updateValueAndValidity({ onlySelf: true, emitEvent: false });
        }
        this.validateBusinessRules();
      });
    });
  }

  private validateBusinessRules(): void {
    if (!this.validationFields.length) return;

    const validationType = this.isCreateMode ? BusinessRuleType.CREATE_VALIDATION : BusinessRuleType.EDIT_VALIDATION;
    const definition = this.entityDefinition();
    const entity = this.entity();

    if (!definition?.validationRules?.some(rule => rule.type === validationType)) {
      return;
    }

    const newExternalErrors: ExternalErrorMap = {};

    this.validationFields.forEach(fieldName => {
      const control = this.form.get(fieldName);
      if (!control) return;

      const formValue = this.form.value as Record<string, unknown>;
      const entityToValidate = {
        ...formValue,
        ...(entity ? { id: entity.id } : { id: undefined }),
      } as unknown as T;

      newExternalErrors[fieldName] = {};

      const rules = definition.validationRules?.filter(rule => rule.type === validationType) ?? [];

      for (const rule of rules) {
        try {
          if (!rule.rule(entityToValidate, this.storeService)) {
            const errorMsg = rule.errorMessageKey ? this.translateService.translate(rule.errorMessageKey) : (rule.errorMessage ?? '');
            newExternalErrors[fieldName] = {
              ...newExternalErrors[fieldName],
              businessRule: errorMsg,
            };
            break;
          }
        } catch (error) {
          console.error(this.translateService.translate('common.errors.businessRuleValidationFailed'), error);
        }
      }
    });

    // Mettre à jour les erreurs externes
    this.externalErrors = newExternalErrors;

    // Si l'initialisation est terminée, forcer une détection de changements
    if (this.initialValidationDone) {
      this.cdr.detectChanges();
    }
  }

  protected deleteConfig() {
    const def = this.entityDefinition();
    const entity = this.entity();
    const name = (entity && def?.deleteName?.(entity)) ?? (entity as { name?: string } | undefined)?.name ?? '';
    return buildDeleteConfig(def?.messages?.confirmDelete?.(), name);
  }

  protected canShowDeleteButton(): boolean {
    return this.entityDefinition()?.formOptions?.showDeleteButton === true && this.entity() !== undefined;
  }

  protected canDeleteEntity(): boolean {
    const entity = this.entity();
    if (!entity) return false;

    const validationRules = this.entityDefinition()?.validationRules;
    if (validationRules) {
      const deleteRules = validationRules.filter(rule => rule.type === BusinessRuleType.DELETE_VALIDATION);

      if (deleteRules.length > 0) {
        return deleteRules.every(rule => rule.rule(entity));
      }
    }

    return true;
  }

  canDeleteEntityMessages(): string {
    const entity = this.entity();
    if (!entity) return '';

    const validationRules = this.entityDefinition()?.validationRules;
    if (validationRules) {
      const deleteRules = validationRules.filter(rule => rule.type === BusinessRuleType.DELETE_VALIDATION);

      if (deleteRules.length > 0) {
        return deleteRules
          .filter(rule => !rule.rule(entity))
          .map(rule =>
            rule.errorMessageKey
              ? this.translateService.translate(rule.errorMessageKey)
              : (rule.errorMessage ?? this.translateService.translate('common.errors.cannotDelete')),
          )
          .join('\n');
      }
    }

    return '';
  }

  protected deleteEntityAndClose(): void {
    const entity = this.entity();
    if (!entity) return;

    if (this.dialogRef) {
      const payload: EntityFormDeleteAction<T> = {
        __action: 'delete',
        entity,
      };
      this.dialogRef.close(payload);
    } else {
      this.deleteRequest.emit(entity);
    }
  }

  protected loadTypeOptions(): void {
    const effectiveAdditionalData = this.additionalData() ?? this.dialogConfig?.data?.additionalData ?? {};

    for (const [key, value] of Object.entries(effectiveAdditionalData)) {
      if (Array.isArray(value)) {
        this.typeOptionsCache[key] = value as SelectOptionList;
      }
    }
  }

  protected getTypeOptions(storeType: string): SelectOptionList {
    return this.typeOptionsCache[storeType] ?? [];
  }

  protected getFormFields(): FieldConfig[] {
    const fields = this.entityDefinition()?.fields ?? [];
    if (!fields.some(f => f.condition)) return fields;
    const formValue = this.form?.getRawValue() as Record<string, unknown> | undefined;
    return fields.filter(f => !f.condition || f.condition(formValue ?? {}));
  }

  protected getFormFieldRows(): FieldConfig[][] {
    const rows: FieldConfig[][] = [];
    let currentGroup: string | undefined;
    for (const field of this.getFormFields()) {
      const lastRow = rows[rows.length - 1];
      if (field.rowGroup && field.rowGroup === currentGroup && lastRow) {
        lastRow.push(field);
      } else {
        rows.push([field]);
        currentGroup = field.rowGroup;
      }
    }
    return rows;
  }

  protected getFieldLabel(field: FieldConfig): string {
    if (field.labelKey) {
      return this.translateService.translate(field.labelKey);
    }
    return field.label ?? '';
  }

  protected getFieldPlaceholder(field: FieldConfig): string {
    if (field.placeholderKey) {
      return this.translateService.translate(field.placeholderKey);
    }
    if (field.placeholder) {
      return field.placeholder;
    }
    return this.getSelectPlaceholder();
  }

  protected getSelectPlaceholder(): string {
    return this.translateService.translate('common.placeholders.select');
  }

  protected hasField(fieldName: string): boolean {
    const entity = this.entity();
    if (entity && fieldName in entity) {
      return true;
    }

    const fields = this.entityDefinition()?.fields;
    if (fields) {
      return fields.some(field => field.field === fieldName);
    }

    return false;
  }

  protected isFieldRequired(fieldName: string): boolean {
    const fields = this.entityDefinition()?.fields;
    if (fields) {
      const field = fields.find(f => f.field === fieldName);
      return field ? field.required : false;
    }

    return false;
  }
  submitForm(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();

      const errors = Object.keys(this.form.controls).reduce<Record<string, ControlDebugInfo>>((acc, key) => {
        const control = this.form.get(key);
        if (control?.invalid) {
          acc[key] = {
            errors: control.errors,
            value: control.value as unknown,
            touched: control.touched,
            dirty: control.dirty,
          };
        }
        return acc;
      }, {});

      console.error(this.translateService.translate('common.errors.formInvalid'), errors);
      return;
    }

    this.submitting = true;
    this.errorMessage = '';

    this.prepareFormBeforeSubmit();

    const formData = this.form.value as EntityFormValue<T>;

    if (this.dialogRef) {
      this.dialogRef.close(formData);
    } else {
      this.formSubmit.emit(formData);
    }
  }

  protected cancel(): void {
    if (this.dialogRef) {
      this.dialogRef.close(false);
    } else {
      this.formCancel.emit();
    }
  }

  protected prepareFormBeforeSubmit(): void {
    Object.values(this.form.controls).forEach((control: AbstractControl) => {
      if (typeof control.value === 'string') {
        control.setValue(control.value.trim(), { emitEvent: false });
      }
    });
  }

  protected getExternalErrorsForField(fieldName: string): FieldErrors {
    return this.externalErrors[fieldName] ?? {};
  }
}

interface ControlDebugInfo {
  errors: ValidationErrors | null;
  value: unknown;
  touched: boolean;
  dirty: boolean;
}
